import Gl from '../../../Gl'

import gsap from '@/lib/gsap'

import { RenderTarget, Camera, Transform, Orbit, Vec3, Box, Mesh, Sphere, Plane, Vec2, Program, Color } from 'ogl'
import cloneGLTF from '../../../Utils/CloneGLTF'
import { gaussianBlur, readDepth, blendOverlay, add, getUVFromGrid } from '../../../Utils/ShaderChunks'

export default class Target {
  constructor(_params) {
    this.gl = new Gl()

    /* 
      Flags
    */
    this.isRendering = true
    this.isVisible = true

    /* 
      Scene
    */
    this.scene = new Transform()

    /* 
      Params
    */
    this.params = _params

    /* 
      Global Uniforms
    */
    this.globalUniforms = {
      uBlurStrength: { value: 40.0 }, // 20.0
      uMinimalBlurStrength: { value: 10.0 }, // 10.0
      uBlurStart: { value: 0.66 },
      uBlurEnd: { value: 0.5 },
    }

    /* 
      Render Targets
    */
    this.sceneRenderTarget = new RenderTarget(this.gl.renderer.instance.gl, {
      width: this.gl.sizes.width * this.gl.sizes.pixelRatio,
      height: this.gl.sizes.height * this.gl.sizes.pixelRatio,
    })

    this.feedbackRenderTargetA = new RenderTarget(this.gl.renderer.instance.gl, {
      width: this.gl.sizes.width * this.gl.sizes.pixelRatio,
      height: this.gl.sizes.height * this.gl.sizes.pixelRatio,
    })

    this.feedbackRenderTargetB = new RenderTarget(this.gl.renderer.instance.gl, {
      width: this.gl.sizes.width * this.gl.sizes.pixelRatio,
      height: this.gl.sizes.height * this.gl.sizes.pixelRatio,
    })

    this.hBlurRenderTarget = new RenderTarget(this.gl.renderer.instance.gl, {
      width: this.gl.sizes.width * this.gl.sizes.pixelRatio,
      height: this.gl.sizes.height * this.gl.sizes.pixelRatio,
    })

    this.feedback = {
      plane: new Mesh(this.gl.renderer.instance.gl, {
        geometry: new Plane(this.gl.renderer.instance.gl),
        program: new Program(this.gl.renderer.instance.gl, {
          uniforms: {
            tCurrentFrame: { value: null },
            tPreviousFrame: { value: null },
            uDecay: { value: 0.65 },
          },
          vertex: /* glsl */ `
              attribute vec2 uv;
              attribute vec3 position;
  
              varying vec2 vUv;
  
              void main() {
                vUv = uv;
                gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
              }
          `,
          fragment: /* glsl */ `
              precision highp float;
  
              varying vec2 vUv;
              uniform sampler2D tCurrentFrame;
              uniform sampler2D tPreviousFrame;
              uniform float uDecay;
  
              void main() {
                  vec4 currentColor = texture2D(tCurrentFrame, vUv);
                  vec4 previousColor = texture2D(tPreviousFrame, vUv);
                  gl_FragColor = currentColor + previousColor * uDecay;
              }
          `,
        }),
      }),
    }

    this.blur = {
      hPlane: new Mesh(this.gl.renderer.instance.gl, {
        geometry: new Plane(this.gl.renderer.instance.gl),
        program: new Program(this.gl.renderer.instance.gl, {
          uniforms: {
            ...this.globalUniforms,
            tMap: { value: null },

            uResolution: { value: new Vec2(this.gl.sizes.width, this.gl.sizes.height) },
            uAspect: { value: this.gl.sizes.aspect },
          },
          vertex: /* glsl */ `
              attribute vec2 uv;
              attribute vec3 position;
  
              varying vec2 vUv;
  
              void main() {
                vUv = uv;

                gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
              }
          `,
          fragment: /* glsl */ `
              precision highp float;
  
              varying vec2 vUv;
              uniform vec2 uResolution;

              uniform sampler2D tMap;


              uniform float uBlurStrength;
              uniform float uMinimalBlurStrength;
              uniform float uBlurStart;
              uniform float uBlurEnd;

              uniform float uAspect;

              ${readDepth}
              ${gaussianBlur}

              void main() {
                vec4 blurredColor = gaussianBlur(tMap, vUv, vec2(1.0, 0.0), uResolution, max(smoothstep(uBlurStart, uBlurEnd, vUv.x) * uBlurStrength, uMinimalBlurStrength), uAspect);

                gl_FragColor = blurredColor;
              }
          `,
        }),
      }),
      vPlane: new Mesh(this.gl.renderer.instance.gl, {
        geometry: new Plane(this.gl.renderer.instance.gl),
        program: new Program(this.gl.renderer.instance.gl, {
          uniforms: {
            ...this.globalUniforms,
            tMap: { value: null },

            uResolution: { value: new Vec2(this.gl.sizes.width, this.gl.sizes.height) },
            uAspect: { value: this.gl.sizes.aspect },
          },
          vertex: /* glsl */ `
              attribute vec2 uv;
              attribute vec3 position;
  
              varying vec2 vUv;
  
              void main() {
                vUv = uv;
                gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
              }
          `,
          fragment: /* glsl */ `
              precision highp float;
  
              varying vec2 vUv;
              uniform vec2 uResolution;

              uniform sampler2D tMap;

              uniform float uBlurStrength;
              uniform float uMinimalBlurStrength;
              uniform float uBlurStart;
              uniform float uBlurEnd;

              uniform float uAspect;

              ${gaussianBlur}              
  
              void main() {
                gl_FragColor = gaussianBlur(tMap, vUv, vec2(0.0, 1.0), uResolution, max(smoothstep(uBlurStart, uBlurEnd, vUv.x) * uBlurStrength, uMinimalBlurStrength), uAspect);
              }
          `,
        }),
      }),
    }

    /* 
      Object Program
    */
    this.program = new Program(this.gl.renderer.instance.gl, {
      uniforms: {
        tMatcap: { value: this.gl.assets.textures.matcaps.glass },
        tGradients: { value: this.gl.assets.textures.gradients },
        tBackground: { value: null },

        uRefractionStrength: { value: 0.9 },
        uGradientIndex: { value: 0.0 },
        uTransparency: { value: 1.0 },

        uReveal: { value: 0.0 },
      },
      vertex: /* glsl */ `
        precision highp float;

        attribute vec2 uv;
        attribute vec3 position;
        attribute vec3 normal;

        uniform mat4 projectionMatrix;
        uniform mat4 modelViewMatrix;
        uniform mat3 normalMatrix;

        uniform float uReveal;

        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec4 vViewPosition;
        varying vec4 vClipPosition;

        void main() {

          vec3 transformed = position;
          transformed.y -= (1.0 - uReveal) * 5.0;

          vec4 clipPosition = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);

          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          vViewPosition = -modelViewMatrix * vec4(transformed, 1.0);
          vClipPosition = clipPosition; 

          gl_Position = clipPosition;
        }
      `,
      fragment: /* glsl */ `
        precision lowp float;

        uniform sampler2D tMatcap;
        uniform sampler2D tGradients;
        uniform sampler2D tBackground;

        uniform float uRefractionStrength;
        uniform float uGradientIndex;
        uniform float uTransparency;
        
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec4 vViewPosition;
        varying vec4 vClipPosition;

        ${getUVFromGrid}
        ${blendOverlay}
        ${add}

        void main() {
          /* 
            Matcap
          */
          vec2 matcapUv = vNormal.xy * 0.5 + 0.5; // 0.495 to remove artifacts caused by undersized matcap disks
          vec3 matcap = texture2D(tMatcap, matcapUv).rgb;

          /* 
            Fresnel
          */
          float fresnel = 1.0 - abs(dot(normalize(vNormal), normalize(vViewPosition.xyz)));
          fresnel = pow(fresnel, 2.0);

          /* 
            Gradient
          */
          vec3 gradient = texture2D(tGradients, getUvFromGrid(vec2(vUv.x, 1.0 - vUv.y), uGradientIndex)).rgb;
          // vec3 gradient = texture2D(tGradients, vUv).rgb;
          // gradient = pow(gradient, vec3(2.2));

          /* 
            Refracted UV
          */
          vec3 refractedBlurryObject = refract(vec3(0.0, 0.0, -1.0), vNormal, uRefractionStrength);
          vec2 uvRefractedBlurryObject = (vClipPosition.xy / vClipPosition.w * 0.5 + 0.5) + refractedBlurryObject.xy;

          /* 
            Blurry Objects
          */
          vec4 textureBlurryObjects = texture2D(tBackground, uvRefractedBlurryObject);
          

          /* 
            Object Color
          */
          // vec3 objectColorWithoutBackground = mix(vec3(0.9), gradient, 0.25);
          // vec3 objectColorWithoutBackground = gradient;
          // vec3 objectColorWithBackground = gradient;
          // vec3 objectColor = mix(objectColorWithoutBackground, objectColorWithBackground, smoothstep(-10.0, 10.0, -vViewPosition.x));

          /* 
            Color
          */
          vec3 color = blendOverlay(add(textureBlurryObjects, vec4(vec3(0.9), 1.0)).rgb + matcap, vec3(fresnel));
          color = mix(gradient, blendOverlay(gradient, color), uTransparency);

          /* 
            Alpha
          */

          // gl_FragColor = vec4(color, max(min(matcap + fresnel, 1.0), max(vUv.y, 0.5)));
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    })

    /* 
      Models
    */
    this.instance = cloneGLTF(this.gl.renderer.instance.gl, this.gl.assets.models.targets)

    this.mouse = this.instance.scene.find((child) => child.name === 'mouse').children[0]
    this.star = this.instance.scene.find((child) => child.name === 'star').children[0]
    this.code = this.instance.scene.find((child) => child.name === 'code').children[0]
    this.warning = this.instance.scene.find((child) => child.name === 'warning').children[0]
    this.compass = this.instance.scene.find((child) => child.name === 'compass').children[0]
    this.cards = this.instance.scene.find((child) => child.name === 'cards').children[0]

    /* 
      Active Model
    */
    this.objects = {
      mouse: this.mouse,
      star: this.star,
      code: this.code,
      warning: this.warning,
      compass: this.compass,
      cards: this.cards,
    }

    for (const key in this.objects) {
      this.objects[key].program = this.program
      this.objects[key].setParent(this.scene)
      this.objects[key].visible = false
    }

    /* 
    Set default
    */
    this.setScale()
    this.setUniforms()
    this.setActiveObject('mouse')
    this.setColor(0)
    this.reveal()
  }

  reveal() {
    for (const key in this.objects) {
      gsap.to(this.objects[key].program.uniforms.uReveal, {
        value: 1.0,
        duration: 2.5,
        ease: 'expo.out',
        delay: 0.5,
      })

      gsap.fromTo(
        this.objects[key].rotation,
        {
          y: Math.PI * 0.25,
        },
        {
          y: 0,
          duration: 2.5,
          ease: 'back.out',
          delay: 0.5,
        }
      )
    }
  }

  setScale() {
    if (window.innerWidth > 991) {
      for (const key in this.objects) {
        this.objects[key].scale.set(1, 1, 1)
      }
    } else {
      for (const key in this.objects) {
        this.objects[key].scale.set(0.75, 0.75, 0.75)
      }
    }
  }

  setUniforms() {
    if (window.innerWidth > 991) {
      this.globalUniforms.uBlurStart.value = 0.66
      this.globalUniforms.uBlurEnd.value = 0.5
    } else {
      this.globalUniforms.uBlurStart.value = 0.4
      this.globalUniforms.uBlurEnd.value = 0.3
    }
  }

  setVisible(_visible) {
    this.isVisible = _visible

    if (this.isVisible && this.active) {
      this.active.visible = true
    } else {
      for (const key in this.objects) {
        this.objects[key].visible = false
      }
    }
  }

  setActiveObject(_active) {
    if (this.active) this.active.visible = false

    if (!this.objects[_active]) return // return if no active object is in the argument

    this.active = this.objects[_active]
    this.active.visible = this.isVisible ? true : false // prevent setting visibility when the full scene is not selected
  }

  setColor(_index, _transparency = 1) {
    for (const key in this.objects) {
      this.objects[key].program.uniforms.uGradientIndex.value = _index
      this.objects[key].program.uniforms.uTransparency.value = _transparency
    }
  }

  resize() {
    this.sceneRenderTarget.setSize(this.gl.sizes.width * this.gl.sizes.pixelRatio, this.gl.sizes.height * this.gl.sizes.pixelRatio)
    this.feedbackRenderTargetA.setSize(this.gl.sizes.width * this.gl.sizes.pixelRatio, this.gl.sizes.height * this.gl.sizes.pixelRatio)
    this.feedbackRenderTargetB.setSize(this.gl.sizes.width * this.gl.sizes.pixelRatio, this.gl.sizes.height * this.gl.sizes.pixelRatio)
    this.hBlurRenderTarget.setSize(this.gl.sizes.width * this.gl.sizes.pixelRatio, this.gl.sizes.height * this.gl.sizes.pixelRatio)

    this.blur.hPlane.program.uniforms.uResolution.value.set(this.gl.sizes.width, this.gl.sizes.height)
    this.blur.vPlane.program.uniforms.uResolution.value.set(this.gl.sizes.width, this.gl.sizes.height)

    this.setScale()
    this.setUniforms()
  }

  renderPipeline() {
    if (!this.isRendering) return

    this.gl.renderer.instance.render({
      scene: this.scene,
      camera: this.params.camera,
      target: this.sceneRenderTarget,
    })

    // Blur background
    // Pass 1: Horizontal blur
    this.blur.hPlane.program.uniforms.tMap.value = this.sceneRenderTarget.texture

    this.gl.renderer.instance.render({
      scene: this.blur.hPlane,
      target: this.hBlurRenderTarget,
    })

    // Pass 2: Vertical blur
    this.blur.vPlane.program.uniforms.tMap.value = this.hBlurRenderTarget.texture

    this.gl.renderer.instance.render({
      scene: this.blur.vPlane,
      target: this.sceneRenderTarget,
    })

    // Feedback pass
    this.feedback.plane.program.uniforms.tCurrentFrame.value = this.sceneRenderTarget.texture
    this.feedback.plane.program.uniforms.tPreviousFrame.value = this.feedbackRenderTargetA.texture

    this.gl.renderer.instance.render({
      scene: this.feedback.plane,
      target: this.feedbackRenderTargetB,
    })

    // Swap buffers for next frame
    const temp = this.feedbackRenderTargetA
    this.feedbackRenderTargetA = this.feedbackRenderTargetB
    this.feedbackRenderTargetB = temp

    // this.output = this.feedbackRenderTargetA
  }

  update() {
    // this.object.position.x = Math.sin(this.gl.time.elapsed)
    // this.object.position.y = Math.cos(this.gl.time.elapsed)
  }
}
